import { useEffect, useRef, useState } from "react";
import { Upload, Image as ImageIcon, Loader2, User, PawPrint, X, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/i18n/I18nProvider";
import { StyleId, ALL_STYLES } from "./StyleSelector";
import { supabase } from "@/integrations/supabase/client";
import { getGeo } from "@/lib/geo";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import heic2any from "heic2any";
import { Pack, Mode, getPrice, MODE_LABEL_KEY, MODE_DESC_KEY } from "@/lib/packPricing";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";
import { trackEvent } from "@/lib/track";
import { PunchFist } from "@/components/PunchFist";

type PackStripe = "single_punch" | "punch_pack";
const PACK_TO_STRIPE: Record<Pack, PackStripe> = { single: "single_punch", triple: "punch_pack" };
const Check = ({ className, strokeWidth }: { className?: string; strokeWidth?: number }) => (
  <svg className={className} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth ?? 2}><polyline points="20 6 9 17 4 12"/></svg>
);

const MODES: { id: Mode; icon: typeof User }[] = [
  { id: "portrait", icon: User },
  { id: "pet", icon: PawPrint },
];



const SUPPORTED_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp", "bmp", "tiff", "tif", "avif"];

function getFileExt(file: File): string {
  return (file.name.split(".").pop() || "").toLowerCase();
}

function isSupported(file: File): boolean {
  const ext = getFileExt(file);
  if (SUPPORTED_EXTS.includes(ext)) return true;
  const type = file.type.toLowerCase();
  return type.startsWith("image/");
}

const MAX_DIMENSION = 1000;
const JPEG_QUALITY = 0.75;

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      // keep url alive until after draw; revoke in caller
      (img as any)._objectUrl = url;
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

async function blobToJpegDataUrl(blob: Blob): Promise<string> {
  const img = await loadImageFromBlob(blob);
  try {
    const longest = Math.max(img.width, img.height);
    const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    // White background so transparent PNGs don't become black on JPEG
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const dataUrl: string = await new Promise((resolve, reject) => {
      if (canvas.toBlob) {
        canvas.toBlob(
          (out) => {
            if (!out) return reject(new Error("Canvas conversion failed"));
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error("FileReader failed"));
            reader.readAsDataURL(out);
          },
          "image/jpeg",
          JPEG_QUALITY
        );
      } else {
        // Safari fallback
        try {
          resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
        } catch (err) {
          reject(err as Error);
        }
      }
    });
    return dataUrl;
  } finally {
    const url = (img as any)._objectUrl as string | undefined;
    if (url) URL.revokeObjectURL(url);
  }
}

async function convertToJpeg(file: File): Promise<string> {
  const ext = getFileExt(file);
  const type = file.type.toLowerCase();

  // HEIC / HEIF → decode via heic2any first, then run through canvas pipeline
  if (ext === "heic" || ext === "heif" || type.includes("heic") || type.includes("heif")) {
    const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
    const jpegBlob = Array.isArray(result) ? result[0] : result;
    return blobToJpegDataUrl(jpegBlob as Blob);
  }

  // Everything else (JPEG, PNG, WEBP, AVIF, BMP, TIFF) → canvas resize + JPEG compress
  return blobToJpegDataUrl(file);
}

export const UploadFlow = ({ style }: { style: StyleId }) => {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pack, setPack] = useState<Pack>("single");
  const [mode, setMode] = useState<Mode>("portrait");
  const [selectedStyles, setSelectedStyles] = useState<StyleId[]>([]);
  const [consent, setConsent] = useState(false);
  const { currency, country } = useGeoCurrency();

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const nextPack = typeof detail === "string" ? detail : detail?.pack;
      const nextMode = typeof detail === "object" ? detail?.mode : undefined;
      if (nextPack === "single" || nextPack === "triple") {
        setPack(nextPack);
        setSelectedStyles([]);
      }
      if (nextMode === "portrait" || nextMode === "scene" || nextMode === "pet") {
        setMode(nextMode);
      }
    };
    window.addEventListener("select-pack", handler);
    return () => window.removeEventListener("select-pack", handler);
  }, []);

  const handleFile = async (file: File) => {
    if (!isSupported(file)) {
      toast({ title: "Unsupported file", description: "Please upload a valid image file.", variant: "destructive" });
      return;
    }
    try {
      const dataUrl = await convertToJpeg(file);
      setPhoto(dataUrl);
    } catch (e) {
      console.error(e);
      toast({ title: "Conversion failed", description: "Could not process this image. Try another format.", variant: "destructive" });
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const toggleStyle = (id: StyleId) => {
    setSelectedStyles((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const stylesToGenerate = (): StyleId[] => (pack === "single" ? [style] : selectedStyles);

  const canGenerate = () => {
    if (!photo || busy || !consent) return false;
    if (pack === "triple" && selectedStyles.length !== 3) return false;
    return true;
  };

  const startCheckout = async () => {
    if (!canGenerate() || !photo) return;
    trackEvent("generate-style");
    setBusy(true);
    try {
      const stripePack = PACK_TO_STRIPE[pack];
      const styles = stylesToGenerate();
      const g = await getGeo().catch(() => ({ country_code: null, country_name: null }) as { country_code: string | null; country_name: string | null });

      // 1) Server uploads the image, creates the order (status=pending_payment).
      const { data: initData, error: initErr } = await supabase.functions.invoke("initiate-order", {
        body: {
          image: photo,
          pack: stripePack,
          selected_styles: styles,
          mode,
          price: getPrice(pack, mode, currency),
          detected_country: country,
          selected_currency: currency,
          country_code: g.country_code,
          country_name: g.country_name,
        },
      });
      if (initErr) throw new Error(initErr.message);
      const orderId = (initData as { order_id?: string } | null)?.order_id;
      if (!orderId) throw new Error("Failed to create order");

      // 2) Create Stripe Checkout Session with server-authoritative pricing.
      const { data: coData, error: coErr } = await supabase.functions.invoke("create-checkout-session", {
        body: { order_id: orderId, pack: stripePack, selected_styles: styles },
      });
      if (coErr) throw new Error(coErr.message);
      const url = (coData as { checkout_url?: string } | null)?.checkout_url;
      if (!url) throw new Error("No checkout URL returned");

      // 3) Redirect to Stripe. Generation begins ONLY after Stripe webhook.
      window.location.href = url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not start checkout";
      toast({ title: "Checkout unavailable", description: msg, variant: "destructive" });
      setBusy(false);
    }
  };

  const packPrice = getPrice(pack, mode, currency);

  return (
    <section id="upload" className="py-20 relative">
      <div className="container max-w-5xl">
        <div className="text-center mb-8 space-y-3">
          <h2 className="font-display text-4xl md:text-5xl font-black">
            <span className="text-gradient">{t("upload.title")}</span>
          </h2>
          <p className="text-gradient text-lg">{t("upload.subtitle")}</p>
        </div>

        {/* Mode selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-2">
          {MODES.map(({ id, icon: Icon }) => {
            const active = mode === id;
            return (
              <button
                key={id}
                onClick={() => setMode(id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition",
                  active
                    ? "border-neon-green text-neon-green glow-green bg-neon-green/10"
                    : "border-border text-muted-foreground hover:border-neon-purple/60"
                )}
              >
                <Icon className="w-4 h-4" />
                {t(MODE_LABEL_KEY[id])}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gradient mb-6">{t(MODE_DESC_KEY[mode])}</p>

        {/* Pack selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {(["single", "triple"] as Pack[]).map((p) => (
            <button
              key={p}
              onClick={() => { setPack(p); setSelectedStyles([]); }}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-bold border-2 transition",
                pack === p
                  ? "border-neon-green text-neon-green glow-green bg-neon-green/10"
                  : "border-border text-muted-foreground hover:border-neon-purple/60"
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                <PunchFist variant={p === "single" ? "green" : "purple"} />
                {t(`pack.${p}`)} — {getPrice(p, mode, currency)}
              </span>
            </button>
          ))}
        </div>

        {/* Punch Pack: pick 3 styles */}
        {pack === "triple" && (
          <div className="mb-6">
            <p className="text-center text-sm text-gradient mb-3">
              {t("pack.pickStyles").replace("{n}", "3")} ({selectedStyles.length}/3)
            </p>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 max-w-5xl mx-auto">
              {ALL_STYLES.map((s) => {
                const active = selectedStyles.includes(s.id);
                const atLimit = selectedStyles.length >= 3 && !active;
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleStyle(s.id)}
                    disabled={atLimit}
                    className={cn(
                      "relative aspect-square rounded-xl overflow-hidden border-2 transition",
                      active
                        ? "border-neon-green scale-[1.03] glow-green"
                        : atLimit
                          ? "border-border opacity-30 cursor-not-allowed"
                          : "border-border hover:border-neon-purple/60 opacity-70 hover:opacity-100"
                    )}
                  >
                    <img src={s.image} alt={t(`style.${s.id}`)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-1 left-1 right-1 text-[10px] font-bold text-left leading-tight">{t(`style.${s.id}`)}</div>
                    {active && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-neon-green grid place-items-center text-background">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedStyles.length !== 3 && (
              <p className="text-center text-xs text-gradient mt-3">
                Select exactly 3 styles to continue ({selectedStyles.length}/3)
              </p>
            )}
          </div>
        )}


        <div className="grid md:grid-cols-2 gap-6">
          {/* Upload area */}
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            className="aspect-square rounded-2xl border-2 border-dashed border-neon-purple/40 bg-card/50 hover:border-neon-purple cursor-pointer transition-all overflow-hidden relative group"
          >
            {photo ? (
              <div className="relative w-full h-full">
                <img src={photo} alt="Your upload" className="w-full h-full object-cover" />
                <button
                  onClick={(e) => { e.stopPropagation(); clearPhoto(); }}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm border border-border text-foreground hover:bg-destructive hover:text-destructive-foreground transition grid place-items-center"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="absolute inset-0 grid place-items-center text-center p-6">
                <div className="space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-neon-purple/10 grid place-items-center group-hover:bg-neon-purple/20 transition">
                    <Upload className="w-7 h-7 text-neon-purple" />
                  </div>
                  <div className="font-semibold">{t("upload.cta")}</div>
                  <div className="text-xs text-gradient">{t("upload.formats")}</div>
                </div>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.bmp,.tiff,.tif,.avif,image/*"
              hidden
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </div>

          {/* Placeholder / info area (previews are delivered after payment on /success) */}
          <div className="aspect-square rounded-2xl border border-border bg-card/50 overflow-hidden relative">
            <div className="absolute inset-0 grid place-items-center text-muted-foreground text-center p-6">
              <div className="space-y-3 max-w-xs">
                <ImageIcon className="w-10 h-10 mx-auto opacity-40" />
                <div className="text-sm font-semibold text-foreground">
                  {pack === "single" ? t(`style.${style}`) : t(`pack.${pack}`)}
                </div>
                <p className="text-xs leading-relaxed text-gradient">
                  {t("upload.deliveryInfo")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 max-w-2xl mx-auto space-y-4">
          <label className="flex gap-3 items-start cursor-pointer text-xs text-muted-foreground leading-relaxed rounded-lg border border-border bg-card/40 p-4">
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              disabled={busy}
              className="mt-0.5 border-neon-purple/60 data-[state=checked]:bg-neon-green data-[state=checked]:text-background data-[state=checked]:border-neon-green"
            />
            <span>
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-neon-purple font-bold hover:text-neon-green transition-colors">Terms &amp; Conditions</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-neon-purple font-bold hover:text-neon-green transition-colors">Privacy Policy</a>
              , and I request that generation of my image begins immediately after payment.
            </span>
          </label>

          <div className="flex flex-col items-center gap-2">
            <Button
              disabled={!canGenerate()}
              onClick={startCheckout}
              size="lg"
              className="bg-neon-green text-background hover:bg-neon-green/90 font-bold h-12 px-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {t("upload.generate")} — {packPrice}
            </Button>
            <p className="text-xs text-muted-foreground text-center">{t("checkout.secure")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
